import { Component, ElementRef, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { JobAction } from '../../../action.types';
import { LoginService } from '@intranet/modules/login';
import { UserService } from '@intranet/modules/users/user.service';
import { User } from '@intranet/modules/users/user.types';
import { PlannedBlockService } from '../planned-block.service';
import { PlannedBlockViewModel } from '../planned-block.types';
import { PlannerCacheService } from '../planner-cache.service';
import { plannerColorForJobNumber } from '../planner-color.util';
import { formatDurationHM, parseDurationHM } from '../planner-date.util';

export interface AddToPlannerDialogData {
    action: JobAction;
}

/** What the dialog closes with once a block has been added. */
export interface AddToPlannerDialogResult {
    block: PlannedBlockViewModel;
}

interface PlannerCandidate {
    userId: string;
    name: string;
}

// "Add to planner" from an action's ⋮ menu on the Actions list: adds a
// planned block for that one action without leaving the list. The same
// fields, rules and look as the Planner's own "Add planned block" popover
// (planner.component — start/duration in 30-minute steps, default today
// 09:00 for 1 hour), copied rather than shared so the Planner is untouched.
// Plus a choice of whose planner it goes on: the action's owners, whoever
// it's waiting on, and the logged-in user.
@Component({
    selector: 'add-to-planner-dialog',
    templateUrl: './add-to-planner-dialog.component.html',
    styleUrls: ['./add-to-planner-dialog.component.scss'],
})
export class AddToPlannerDialogComponent {
    private static readonly STEP_MINUTES = 30;
    private static readonly LAST_START_MINUTES = 24 * 60 - AddToPlannerDialogComponent.STEP_MINUTES; // 23:30

    readonly action: JobAction;
    readonly candidates: PlannerCandidate[];
    // The same people, for the Planner dropdown (the app's user picker).
    readonly candidateUsers: User[];
    userId: string | null;

    day: Date;
    // From the list, a block is for today or later only (what the Planned
    // flag counts) — earlier days are greyed out in the day picker.
    readonly today: Date;
    dayPickerOpen = false;
    dayPickerUpward = false;
    startMinutes = 9 * 60;
    startHText = '09';
    startMText = '00';
    durationMinutes = 60;
    durationText = '1h';
    editingDuration = false;
    error = '';
    saving = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: AddToPlannerDialogData,
        private _dialogRef: MatDialogRef<AddToPlannerDialogComponent, AddToPlannerDialogResult>,
        private _plannedBlockService: PlannedBlockService,
        private _plannerCache: PlannerCacheService,
        private _loginService: LoginService,
        private _userService: UserService,
        private _el: ElementRef<HTMLElement>
    ) {
        this.action = data.action;
        this.candidates = this.buildCandidates();
        const users = this._userService.users || [];
        this.candidateUsers = this.candidates
            .map((c) => users.find((u) => u.id === c.userId))
            .filter((u): u is User => !!u);
        this.userId = this.defaultUserId();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.today = today;
        this.day = new Date(today);
    }

    /** The only person it can go on, when that's who it's on: shown as a
     *  plain field instead of a one-option picker (singlePlanner). */
    get singlePlannerUser(): User | null {
        return this.candidateUsers.length === 1 && this.candidateUsers[0].id === this.userId
            ? this.candidateUsers[0]
            : null;
    }

    get jobDotColor(): string {
        return plannerColorForJobNumber(this.action.jobNumber).titleColor;
    }

    get actionTitle(): string {
        const t = this.action.title || '';
        return t.charAt(0).toUpperCase() + t.slice(1);
    }

    // Owners first (in the order they're assigned), then whoever it's
    // waiting on, then the logged-in user — each once, active people only.
    // Without the Planner role, that's just the logged-in user.
    private buildCandidates(): PlannerCandidate[] {
        const me = this._loginService.userId;
        const ids = [
            ...(this.action.assignedUsers || []),
            ...(this.action.waitingUserId ? [this.action.waitingUserId] : []),
            ...(me ? [me] : []),
        ];
        // Only planners this user may change: their own, or anyone's with
        // the Planner role (spec, Access and Roles — the server checks too).
        const isPlanner = this._loginService.checkAuth(['Planner']);
        const seen = new Set<string>();
        const result: PlannerCandidate[] = [];
        ids.forEach((id) => {
            if (!id || seen.has(id)) return;
            if (!isPlanner && id !== me) return;
            seen.add(id);
            const user = (this._userService.users || []).find((u) => u.id === id);
            if (user && !user.active && id !== me) return;
            const name = user ? (user.fullName || `${user.firstName} ${user.surname}`) : '';
            result.push({ userId: id, name: name || 'Me' });
        });
        return result;
    }

    // The logged-in user's own planner when it's their action (owner or
    // waited on); otherwise its first owner's; otherwise their own.
    private defaultUserId(): string | null {
        const me = this._loginService.userId;
        const involved = [...(this.action.assignedUsers || []), this.action.waitingUserId];
        if (me && involved.includes(me)) return me;
        return this.action.assignedUsers?.find((id) => this.candidates.some((c) => c.userId === id)) ?? me ?? null;
    }

    selectUser(userId: string | null): void {
        if (!userId) return; // the picker has no "clear" here
        this.userId = userId;
        this.error = '';
    }

    // ─── Day ──────────────────────────────────────────────────────────────

    // Opens below the Day link, or above it when there isn't room below —
    // same rule as the Planner's popover.
    toggleDayPicker(e: MouseEvent): void {
        this.dayPickerOpen = !this.dayPickerOpen;
        if (!this.dayPickerOpen) return;
        const PICKER_H = 230;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        this.dayPickerUpward = rect.bottom + PICKER_H > document.documentElement.clientHeight - 20;
    }

    onDayPicked(day: Date): void {
        this.day = day;
        this.dayPickerOpen = false;
    }

    // ─── Start ────────────────────────────────────────────────────────────

    private roundToStep(minutes: number): number {
        const step = AddToPlannerDialogComponent.STEP_MINUTES;
        return Math.round(minutes / step) * step;
    }

    private setStart(minutes: number): void {
        this.startMinutes = Math.min(AddToPlannerDialogComponent.LAST_START_MINUTES, Math.max(0, this.roundToStep(minutes)));
        this.startHText = String(Math.floor(this.startMinutes / 60)).padStart(2, '0');
        this.startMText = String(this.startMinutes % 60).padStart(2, '0');
    }

    // HH and MM read together, then rounded to the nearest half hour.
    // Anything unreadable falls back to the last good start.
    commitStart(): void {
        const h = parseInt(this.startHText, 10);
        const m = parseInt(this.startMText || '0', 10);
        if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
            this.setStart(this.startMinutes);
            return;
        }
        this.setStart(h * 60 + m);
    }

    // Digits only, two per segment; a full hour moves on to the minutes.
    onSegInput(e: Event, seg: 'h' | 'm'): void {
        const input = e.target as HTMLInputElement;
        const digits = (input.value || '').replace(/\D/g, '').slice(0, 2);
        input.value = digits;
        if (seg === 'h') {
            this.startHText = digits;
            if (digits.length === 2) this.focusSeg('m');
        } else {
            this.startMText = digits;
        }
    }

    // ↑/↓: an hour on HH, half an hour on MM. Enter adds the block.
    onSegKeydown(e: KeyboardEvent, seg: 'h' | 'm'): void {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitStart();
            const step = seg === 'h' ? 60 : AddToPlannerDialogComponent.STEP_MINUTES;
            this.setStart(this.startMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.submit();
        }
    }

    // Once focus has left both segments, tidy and round what was typed.
    onSegBlur(): void {
        setTimeout(() => {
            const active = document.activeElement as HTMLElement | null;
            if (active?.classList.contains('AddToPlanner-seg')) return;
            this.commitStart();
        }, 0);
    }

    private focusSeg(seg: 'h' | 'm'): void {
        const el = this._el.nativeElement.querySelector(`.AddToPlanner-seg[data-seg="${seg}"]`) as HTMLInputElement | null;
        el?.focus();
        el?.select();
    }

    // ─── Duration ─────────────────────────────────────────────────────────

    private setDuration(minutes: number): void {
        this.durationMinutes = Math.max(AddToPlannerDialogComponent.STEP_MINUTES, this.roundToStep(minutes));
        this.durationText = formatDurationHM(this.durationMinutes); // "1h 30m"
    }

    // "1h 30m", "1h", "30m" (or "1:30", "1.5") — same reading as the Planner's.
    commitDuration(): void {
        this.setDuration(parseDurationHM(this.durationText) ?? this.durationMinutes);
    }

    startEditingDuration(): void {
        this.editingDuration = true;
        setTimeout(() => {
            const el = this._el.nativeElement.querySelector('.AddToPlanner-durInput') as HTMLInputElement | null;
            el?.focus();
            el?.select();
        }, 0);
    }

    stopEditingDuration(): void {
        this.commitDuration();
        this.editingDuration = false;
    }

    // ↑/↓ step by 30 mins, Enter adds.
    onDurationKeydown(e: KeyboardEvent): void {
        const step = AddToPlannerDialogComponent.STEP_MINUTES;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitDuration();
            this.setDuration(this.durationMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.submit();
        }
    }

    // ─── Add / close ──────────────────────────────────────────────────────

    close(): void {
        this._dialogRef.close();
    }

    // Same save as the Planner's own: the person's cached blocks are cleared
    // either side, so the Planner shows the new block next time it's opened.
    submit(): void {
        if (this.saving) return;
        this.commitStart();
        this.commitDuration();
        const userId = this.userId;
        const jobActionId = this.action.jobActionId;
        if (!userId) {
            this.error = 'Please choose whose planner.';
            return;
        }
        if (!jobActionId) return;

        const start = new Date(this.day);
        start.setHours(0, this.startMinutes, 0, 0);
        const durationMinutes = this.durationMinutes;

        this.saving = true;
        this.error = '';
        this._plannerCache.invalidateBlocks(userId);
        this._plannedBlockService.createPlannedBlock({ jobActionId, userId, start, durationMinutes }).subscribe({
            next: (block) => {
                this._plannerCache.invalidateBlocks(userId);
                this._dialogRef.close({ block });
            },
            error: () => {
                this._plannerCache.invalidateBlocks(userId);
                this.saving = false;
                this.error = 'Could not add this block. Please try again.';
            },
        });
    }
}
